import React, { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { get } from 'lodash';
import { ChevronLeft, Close as CloseIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { Tooltip, ClickAwayListener, Divider } from '@mui/material';

import { useSelector } from 'react-redux';
import { Button } from '../../../common/components/Button/Button';
import { ChargerLastUpdate } from '../../../common/components/ChargerLastUpdate';
import Menu from '../../../common/components/Menu/Menu';
import MenuItem from '../../../common/components/MenuItem/MenuItem';
import { Toolbar } from '../../../common/components/Toolbar/Toolbar';
import { getFWUpdateButtonLabel, getResetButtons, getResetButtonsMenu } from '../../../common/constants/resetButtons';
import hasAccess, { createChargerTaskAccessId, firmwareUpdateBtnAccessId } from '../../../common/services/HasAccess';
import { isMobile } from '../../../common/services/isMobile';
import ChargerTaskCreatingDialog from './ChargerTaskCreatingDialog';
import FirmwareUpdateDialog from './FirmwareUpdateDialog';
import ResetButtons from './ResetButtons';
import ResetDialog from './ResetDialog';
import MappedLabel from '../../../common/components/MappedLabel/MappedLabel';
import { permissions } from '../../../common/constants/permissions';
import { selectIsConfigurationLockedByAnotherUser } from '../selectors';
import {
  CHANGES_PENDING,
  CONFIGURATION_REBOOT_REQUIRED,
  CHARGER_FIRMWARE_SENT,
  EV_STATUS_CONNECTED,
} from '../../../common/constants/statuses';
import { StatusLabel } from '../../../common/constants/CommonUsedDropdownsStatics';
import TransferButton from '../../../common/components/TransferButton';
import { multiFwUpdateTableId } from '../../MultiFwUpdate/constants';
import { selectFWUpdatesById } from '../../../common/components/SSPList/selectors';
import { IN_PROGRESS_CHARGER_TASK_STATUS } from '../../../common/constants/chargerTask';
import { selectConfigurationNodeAvailability } from '../../../common/components/appConfig/selector';
import { selectUserPermissions } from '../../Login/selectors';

export const titleStyle = {
  fontWeight: 700,
};

const requiredForViewConfigurationStatuses = [CONFIGURATION_REBOOT_REQUIRED, CHANGES_PENDING];
const fwActiveChargingSessions = [CHARGER_FIRMWARE_SENT, IN_PROGRESS_CHARGER_TASK_STATUS];

function ChargerDetailsToolbar(props) {
  const {
    currentChargerData,
    isFetching,
    config,
    pristine,
    dirty,
    submitting,
    updateTimestamp,
    updateReason,
    readOnly,
    submit,
    onClose,
    user,
    resetErrorSnackbar,
    resetSuccessSnackbar,
    openResetDialogFlag,
    openResetDialog,
    resetCommand,
    resetError,
    toggleSnackbar,
    closeResetDialog,
    resetCharger,
    chargerId,
    activeTabValue,
    configurationForm,
    isDiagnosticsAvailable,
    isNonLiveConfigurationParametersExists,
    getPlates,
    clearMultiFwTableFilters,
  } = props;

  const [openResetPopover, setOpenResetPopover] = useState(false);
  const [createTaskDialogIsShown, setCreateTaskDialogIsShown] = useState(false);
  const anchorEl = useRef(null);
  const isConfigurationLockedByAnotherUser = useSelector(selectIsConfigurationLockedByAnotherUser);
  const { chargerId: urlChargerId } = useParams();
  const uuid = chargerId || urlChargerId;
  const fwUpdateChargerInfo = useSelector(selectFWUpdatesById(uuid));
  const isNodeConfigurationParametersAvailable = useSelector(selectConfigurationNodeAvailability);
  const userPermissions = useSelector(selectUserPermissions);

  const hasSupervisorPermission =
    (user.currentPermissions && user.currentPermissions.includes(permissions.SUPERVISOR)) ||
    user.initialRoles.some(({ roleName }) => roleName.toUpperCase() === permissions.SUPERVISOR);
  const isCustomerFromCurrentCharger = user.customer_id === get(currentChargerData, 'customer.customerId');
  const configurationStatus = String(get(currentChargerData, 'configuration_status')).toLowerCase();
  const timestampFormatted = updateTimestamp ? `${updateTimestamp} UTC` : '';
  const showFirmwareUpdateButton = hasAccess(firmwareUpdateBtnAccessId, get(user, 'currentPermissions'), config);
  const isOnline = get(currentChargerData, 'i2p2_state.c_online');
  const chargerFullStateTypes = get(currentChargerData, 'c_full_state', []).filter(({ connector_id }) => connector_id !== 0);
  const hasConnectedEv = chargerFullStateTypes.some(({ connector_ev_connection }) => connector_ev_connection === EV_STATUS_CONNECTED);
  const fwUpdateButtonLabel = getFWUpdateButtonLabel(config);
  const title = `Charger: ${uuid}`;
  const mobileTitle = uuid;
  const fwStatus = get(fwUpdateChargerInfo, 'firmware_update.f_status', '');
  const isFwStatusInProgress = fwActiveChargingSessions.includes(fwStatus);
  const isTransferButtonDisabled =
    currentChargerData?.plaza_name ||
    requiredForViewConfigurationStatuses.includes(configurationStatus) ||
    hasConnectedEv ||
    !isOnline ||
    isFwStatusInProgress;
  const isSupervisorOrFullAccess = userPermissions.includes(permissions.SUPERVISOR) || userPermissions.includes(permissions.FULL_ACCESS);
  const isVisibleChargerConfiguration =
    isNodeConfigurationParametersAvailable &&
    (isSupervisorOrFullAccess || userPermissions.includes(permissions.VIEW_CHARGER_CONFIGURATION));

  const reset = useMemo(() => props.reset.bind(null, 'chargerDetails'), []);
  const openFirmwareUpdate = useMemo(() => props.toggleFirmwareUpdateModal.bind(null, true), []);
  const closeFirmwareUpdate = useMemo(() => props.toggleFirmwareUpdateModal.bind(null, false), []);

  const showCreateTaskDialog = () => setCreateTaskDialogIsShown(true);
  const hideCreateTaskDialog = () => setCreateTaskDialogIsShown(false);
  const moreActionsClickHandler = (event) => {
    event.preventDefault();

    setOpenResetPopover(true);
    anchorEl.current = event.currentTarget;
  };

  const moreActionsRequestClose = () => setOpenResetPopover(false);

  const handleMobileFirmwareUpdate = () => {
    openFirmwareUpdate();
    moreActionsRequestClose();
  };

  const fetchPlates = async (uuid) => {
    try {
      await getPlates(multiFwUpdateTableId, uuid);
      clearMultiFwTableFilters()
    } catch {
      // error handler
    }
  };

  useEffect(() => {
    props.clearFirmwareUpdateModal();
    props.checkInteractionDiscovery();
    props.unlockConfig(uuid);
    fetchPlates(uuid);
  }, []);

  const submitButton =
    activeTabValue === 'configuration'
      ? !readOnly && (
          <Button
            className="table-button-with-span"
            disabled={configurationForm.pristine || configurationForm.invalid}
            onClick={props.onConfigurationSubmit}
          >
            Submit
          </Button>
        )
      : !readOnly && (
          <Button className="table-button-with-span" disabled={pristine || submitting} onClick={submit}>
            Submit
          </Button>
        );

  const backButton = (
    <Tooltip title="Back To List">
      <Button className="table-button back-button" onClick={onClose}>
        <ChevronLeft />
      </Button>
    </Tooltip>
  );

  const transferButton = <TransferButton disabled={isTransferButtonDisabled} chargerId={currentChargerData?.c_uuid} />;

  const cancelButton =
    activeTabValue === 'configuration'
      ? !readOnly && (
          <Button
            className="table-button"
            disabled={configurationForm.pristine}
            onClick={() => props.onConfigurationReset(get(currentChargerData, 'c_uuid'))}
          >
            <CloseIcon /> {!isMobile && 'Cancel'}
          </Button>
        )
      : !readOnly && (
          <Button className="table-button" disabled={!dirty} onClick={reset}>
            <CloseIcon /> {!isMobile && 'Cancel'}
          </Button>
        );

  const firmwareUpdateButton = (
    <Button
      className="table-button-with-span"
      disabled={
        isFetching ||
        readOnly ||
        isNonLiveConfigurationParametersExists ||
        !configurationForm.pristine ||
        isConfigurationLockedByAnotherUser
      }
      onClick={openFirmwareUpdate}
    >
      {fwUpdateButtonLabel}
    </Button>
  );

  const createTaskButton =
    props.showChargerTasks && hasAccess(createChargerTaskAccessId, props.userPermissions) ? (
      <Button className="table-button-with-span" disabled={!isDiagnosticsAvailable || isFetching} onClick={showCreateTaskDialog}>
        <MappedLabel reserve="Create task">chargerDetails.createTaskBtnLabel</MappedLabel>
      </Button>
    ) : null;

  const createTaskButtonMobile =
    props.showChargerTasks && hasAccess(createChargerTaskAccessId, props.userPermissions) ? (
      <div>
        <MenuItem
          disabled={!isDiagnosticsAvailable || isFetching}
          onClick={() => {
            showCreateTaskDialog();
            moreActionsRequestClose();
          }}
          title="Create task"
        >
          <MappedLabel reserve="Create task">chargerDetails.createTaskBtnLabel</MappedLabel>
        </MenuItem>
      </div>
    ) : null;

  const firmwareUpdateButtonMobile = (
    <div>
      <MenuItem
        disabled={isFetching || readOnly || isConfigurationLockedByAnotherUser}
        onClick={() => handleMobileFirmwareUpdate()}
        title={fwUpdateButtonLabel}
      >
        {fwUpdateButtonLabel}
      </MenuItem>
      <Divider />
    </div>
  );

  const timestampWithTooltip = <ChargerLastUpdate updateReason={updateReason} timeToShow={timestampFormatted} />;
  const configurationParametersIndicator = requiredForViewConfigurationStatuses.includes(configurationStatus) && (
    <StatusLabel status={configurationStatus} />
  );

  const mainToolbarButtons = (
    <>
      <ResetButtons {...props} isFetching={!!isFetching} />
      <span className="toolbar-separator" />
      {showFirmwareUpdateButton && firmwareUpdateButton}
      {createTaskButton}
    </>
  );

  const desktopToolbar = (
    <Toolbar className="chargers-toolbar">
      <div className="toolbar-group">
        {backButton}
        <span className="toolbar-separator" />
        <div style={titleStyle}>{title}</div>
        <span className="toolbar-separator" />
        {cancelButton}
        {submitButton}
        {hasSupervisorPermission && !isCustomerFromCurrentCharger ? null : mainToolbarButtons}
        {transferButton}
      </div>
      <div className="toolbar-group">
        {isVisibleChargerConfiguration && configurationParametersIndicator}
        {timestampWithTooltip}
      </div>
    </Toolbar>
  );

  const resetButtons = getResetButtons(currentChargerData, config, user.currentPermissions);
  const resetButtonsMenuItems = getResetButtonsMenu(resetButtons, moreActionsRequestClose, openResetDialog, !isOnline || isFetching);
  const showResetButtons = resetButtons.length > 0;
  const actionsWrapperStyle = { display: 'inline-block' };
  const mainPopoverButtons = (
    <>
      {createTaskButtonMobile}
      {showFirmwareUpdateButton && firmwareUpdateButtonMobile}
      {resetButtonsMenuItems}
    </>
  );
  const actionsPopover = showResetButtons && (
    <ClickAwayListener onClickAway={moreActionsRequestClose}>
      <div style={actionsWrapperStyle} className="table-button-with-span-wrapper">
        <Button className="table-button-with-span" onClick={moreActionsClickHandler} disabled={readOnly}>
          <ExpandMoreIcon /> More Actions
        </Button>
        <Menu open={openResetPopover} anchorEl={anchorEl.current} placement="bottom-start">
          {hasSupervisorPermission && !isCustomerFromCurrentCharger ? null : mainPopoverButtons}
        </Menu>
      </div>
    </ClickAwayListener>
  );

  const mobileToolbar = (
    <Toolbar className="chargers-toolbar-mobile">
      <div className="toolbar-group">
        {backButton}
        {cancelButton}
        {submitButton}
        {actionsPopover}
      </div>
      <div className="toolbar-group">
        <div className="title">{mobileTitle}</div>
        {isVisibleChargerConfiguration && configurationParametersIndicator}
        {timestampWithTooltip}
      </div>
    </Toolbar>
  );

  return (
    <div>
      {createTaskDialogIsShown && <ChargerTaskCreatingDialog chargerId={uuid} isOpen close={hideCreateTaskDialog} />}
      <FirmwareUpdateDialog onCancel={closeFirmwareUpdate} c_uuid={uuid} c_online={isOnline} />
      <ResetDialog
        resetErrorSnackbar={resetErrorSnackbar}
        resetSuccessSnackbar={resetSuccessSnackbar}
        openResetDialogFlag={openResetDialogFlag}
        closeResetDialog={closeResetDialog}
        resetCommand={resetCommand}
        resetCharger={resetCharger}
        config={config}
        c_uuid={uuid}
        resetError={resetError}
        toggleSnackbar={toggleSnackbar}
      />
      {isMobile ? mobileToolbar : desktopToolbar}
    </div>
  );
}

ChargerDetailsToolbar.propTypes = {
  closeResetDialog: PropTypes.func,
  config: PropTypes.object,
  currentChargerData: PropTypes.object,
  dirty: PropTypes.bool,
  isFetching: PropTypes.bool,
  onClose: PropTypes.func,
  openResetDialog: PropTypes.func,
  openResetDialogFlag: PropTypes.bool,
  pristine: PropTypes.bool,
  readOnly: PropTypes.bool,
  reset: PropTypes.func,
  resetCharger: PropTypes.func,
  resetCommand: PropTypes.string,
  resetError: PropTypes.string,
  resetErrorSnackbar: PropTypes.bool,
  resetSuccessSnackbar: PropTypes.bool,
  submit: PropTypes.func,
  submitting: PropTypes.bool,
  timestamp: PropTypes.string,
  title: PropTypes.string,
  toggleFirmwareUpdateModal: PropTypes.func,
  toggleSnackbar: PropTypes.func,
  isNonLiveConfigurationParametersExists: PropTypes.bool,
  user: PropTypes.object,
};

export default ChargerDetailsToolbar;
